import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const MAX_NAME_LENGTH = 100;

export interface SaveSearchDialogProps {
  readonly open: boolean;
  readonly existingNames: string[];
  readonly onSubmit: (name: string) => void;
  readonly onClose: () => void;
}

export default function SaveSearchDialog({
  open,
  existingNames,
  onSubmit,
  onClose,
}: SaveSearchDialogProps) {
  const { t } = useTranslation(["search"]);
  const [name, setName] = useState<string>("");

  // Reset state each time the dialog opens.
  useEffect(() => {
    if (open) setName("");
  }, [open]);

  const trimmed = name.trim();
  const isEmpty = trimmed.length === 0;
  const isTooLong = trimmed.length > MAX_NAME_LENGTH;
  const isDuplicate = existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase());

  let helperText: string | null = null;
  if (isTooLong) helperText = t("search:savedSearch.v2.dialogNameTooLong");
  else if (isDuplicate) helperText = t("search:savedSearch.v2.dialogDuplicateName");

  const submitDisabled = isEmpty || isTooLong || isDuplicate;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("search:savedSearch.v2.dialogSaveAsTitle")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          fullWidth
          label={t("search:savedSearch.v2.dialogNameLabel")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!helperText}
          helperText={helperText ?? " "}
          inputProps={{ maxLength: MAX_NAME_LENGTH + 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("search:savedSearch.v2.cancel")}</Button>
        <Button
          variant="contained"
          disabled={submitDisabled}
          onClick={() => onSubmit(trimmed)}
        >
          {t("search:savedSearch.v2.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
